import {
  type CreateShareGrantInput,
  type CreateShareLinkInput,
  type DocumentShareGrants,
  type DocumentShareLinkItem,
  type DocumentShareLinks,
  type EnabledShareMode,
  type OrgMemberHits,
  type SharedCopyResult,
  type SharedDocument,
  type SharedFileUrls,
  type SharedGrantOfFile,
  type SharedWithMe,
  type ShareSnapshotPresign,
  type ShareSnapshotRef,
} from '@apm/contracts';
import { apiFetch } from '../../../lib/http';

export const shareKeys = {
  all: ['me', 'shares'] as const,
  links: (fileId: string) => ['me', 'shares', 'links', fileId] as const,
  grants: (fileId: string) => ['me', 'shares', 'grants', fileId] as const,
  people: (q: string) => ['me', 'shares', 'people', q] as const,
  sharedWithMe: ['me', 'shares', 'shared-with-me'] as const,
  grantOfFile: (fileId: string) => ['me', 'shares', 'grant-of-file', fileId] as const,
};

/** Chia sẻ tài liệu cá nhân — APM-SHR-b…e. `fileId` là id tệp đang mở ở "Tệp của tôi". */
export const shareApi = {
  links: (fileId: string) => apiFetch<DocumentShareLinks>(`/me/files/${fileId}/shares`),

  createLink: (fileId: string, input: CreateShareLinkInput) =>
    apiFetch<DocumentShareLinkItem>(`/me/files/${fileId}/shares`, { method: 'POST', body: input }),

  revokeLink: (fileId: string, linkId: string) =>
    apiFetch<void>(`/me/files/${fileId}/shares/${linkId}`, { method: 'DELETE' }),

  presignSnapshot: (fileId: string, sizeBytes: number) =>
    apiFetch<ShareSnapshotPresign>(`/me/files/${fileId}/shares/snapshot-presign`, {
      method: 'POST',
      body: { sizeBytes },
    }),

  /** Bản sạch (không bình luận) của tài liệu — APM-CMT-d; object tải lên bằng `presignSnapshot`. */
  putCleanCopy: (fileId: string, copy: ShareSnapshotRef) =>
    apiFetch<DocumentShareLinks>(`/me/files/${fileId}/shares/clean-copy`, {
      method: 'PUT',
      body: copy,
    }),

  refreshSnapshot: (fileId: string, linkId: string, snapshot: ShareSnapshotRef) =>
    apiFetch<DocumentShareLinkItem>(`/me/files/${fileId}/shares/${linkId}/snapshot`, {
      method: 'PUT',
      body: snapshot,
    }),

  grants: (fileId: string) => apiFetch<DocumentShareGrants>(`/me/files/${fileId}/grants`),

  grant: (fileId: string, input: CreateShareGrantInput) =>
    apiFetch<DocumentShareGrants>(`/me/files/${fileId}/grants`, { method: 'POST', body: input }),

  changeGrant: (fileId: string, grantId: string, permission: EnabledShareMode) =>
    apiFetch<DocumentShareGrants>(`/me/files/${fileId}/grants/${grantId}`, {
      method: 'PATCH',
      body: { permission },
    }),

  revokeGrant: (fileId: string, grantId: string) =>
    apiFetch<DocumentShareGrants>(`/me/files/${fileId}/grants/${grantId}`, { method: 'DELETE' }),

  people: (q: string) =>
    apiFetch<OrgMemberHits>(`/me/share-people?${new URLSearchParams({ q }).toString()}`),

  sharedWithMe: () => apiFetch<SharedWithMe>('/me/shared-with-me'),

  /** Lượt chia sẻ cho tôi của một tệp — link `/files?preview=` của chủ tệp ở người nhận (APM-PVLINK). */
  grantOfFile: (fileId: string) =>
    apiFetch<SharedGrantOfFile>(`/me/shared-with-me/by-file/${fileId}`),
};

/** Người xem mở tài liệu được chia sẻ: qua link (có / không đăng nhập) hoặc qua lượt cấp quyền. */
export type ShareTarget =
  | { readonly kind: 'link'; readonly token: string; readonly signedIn: boolean }
  | { readonly kind: 'grant'; readonly grantId: string };

function basePath(target: ShareTarget): string {
  if (target.kind === 'grant') return `/me/shared-with-me/${target.grantId}`;
  // Chưa đăng nhập: chỉ cửa công khai mở được — link khác trả 401, trang mời đăng nhập.
  return target.signedIn ? `/shares/${target.token}` : `/public/shares/${target.token}`;
}

export const sharedDocumentApi = {
  open: (target: ShareTarget) => apiFetch<SharedDocument>(basePath(target)),

  urls: (target: ShareTarget, ids: readonly string[]) =>
    apiFetch<SharedFileUrls>(`${basePath(target)}/urls`, { method: 'POST', body: { ids } }),

  copy: (target: ShareTarget) =>
    apiFetch<SharedCopyResult>(`${basePath(target)}/copy`, { method: 'POST' }),
};
